import { useMemo } from 'react';
import {
  useCurrentWallet,
  thirdPartyWallet,
  useChainList,
  useConnectWalletModal,
  useCreateWalletModal,
  userInfoStore,
  userCloudWalletStore,
  useCloudWallet,
  useUserInfo,
} from '@web3fe/infra';
import {
  WalletChainTypes,
  WalletInfo,
  WalletType,
  WalletAddress,
  BybitWalletTypes,
  Chain,
} from '@web3/base-utils';

type walletStore = {
  // walletId，只有bybit钱包有
  walletId: string;
  chainId: number;
  // walletType
  walletType: WalletType;
  walletTypeByKey: keyof typeof WalletType;
  // evm钱包信息
  evmWalletInfo: WalletInfo;
  // 链列表
  chainList: Chain[];
  mnemonicChainList: Chain[];
  // 钱包是否ready
  walletIsReady: boolean;
  // 现货账号是否raady
  spotIsReady: boolean;
  // 当前是否为云钱包
  isBrowserWallet: boolean;
  // 当前是否为第三方钱包
  isThird: boolean;
  mixedChainMap: Record<string, Chain>;
  // 当前是否为bybit插件钱包
  isBybitWalletExtension: boolean;
  // 当前是否为bybit钱包
  isBybitWallet: boolean;
  // 是否连了三方钱包
  thirdConnected: boolean;
  // 是否登陆了云账号，可能登陆了但是无钱包
  cloudConnected: boolean;
  userInfo: any;
  // 是否登陆，包含没创建钱包的情况
  isLogin: boolean;
  // 是否有钱包
  hasWallet: boolean;
  // 登陆了云账号，是否有云钱包
  isCreatedCloudWallet: boolean;
  // 当前是否需要创建云钱包
  needCreatedCloudWallet: boolean;
  evmThirdPartyProvider: any;
  stxThirdPartyProvider: any;
  solanaThirdPartyProvider: any;
  tonThirdPartyProvider: any;
  // addresssMap
  walletAddresses: WalletAddress[];
  // 切换到云钱包
  switchToBrowser: any;
  // 切换到三方钱包
  switchToThirdParty: any;
  // web3统一登陆
  openLoginModal: any;
  openSpotLoginModal: any;
  // 唤起bybit插件钱包连接
  connectBybitWallet: any;
  // 创建钱包
  createWallet: any;
  // 断连三方钱包
  disconnectThird: any;
  // 切换链
  switchChain: (chainId: number) => Promise<any>;
  getAllBalance: any;
};

export const useWallet = (): walletStore => {
  const {
    walletId,
    walletIsReady,
    isBrowserWallet,
    evmAddress,
    name,
    isThirdParty,
    switchToBrowser,
    btcAddress,
    solAddress,
    suiAddress,
    stxAddress,
    switchToThirdParty,
    switchToBybitExtension,
    getAllBalance,
  } = useCurrentWallet();

  const { show: connectWalletShow } = useConnectWalletModal();
  const { show: createWalletShow } = useCreateWalletModal();
  const { cloudWalletList } = useCloudWallet();
  const { isLogin: spotIsLogin, isReady: spotIsReady } = useUserInfo();
  const { chainMap, mnemonicChainList, chainList, mixedChainMap } =
    useChainList();
  // 包含未创建钱包的情况
  const isLogin = thirdPartyWallet.hasAddress || spotIsLogin;
  // 只有需要创建的情况下，值为true
  const needCreatedCloudWallet =
    !isThirdParty && spotIsLogin && !userCloudWalletStore.state.hasCloudWallet;

  const walletType = (() => {
    if (isBrowserWallet) {
      return WalletType.CLOUD;
    }

    if (isThirdParty && thirdPartyWallet.isBybitWalletExtension) {
      return thirdPartyWallet.activeWalletHub?.walletType;
    }

    if (isThirdParty && !thirdPartyWallet.isBybitWalletExtension) {
      return WalletType.THIRD_PARTY;
    }

    // 登陆了账号但是无云钱包
    if (userCloudWalletStore?.state?.userInfo) {
      return WalletType.CLOUD;
    }

    return WalletType.NOT;
  })();

  const walletAddresses = useMemo(() => {
    const addresses: WalletAddress[] = [];
    if (isThirdParty) {
      mnemonicChainList.forEach((item: any) => {
        let address = '';

        if (item.chainType === WalletChainTypes.BTC) {
          address = btcAddress;
        } else if (item.chainType === WalletChainTypes.SOLANA) {
          address = solAddress;
        } else if (item.chainType === WalletChainTypes.SUI) {
          address = suiAddress;
        } else if (item.chainType === WalletChainTypes.STACKS) {
          address = stxAddress;
        } else if (item.chainType === WalletChainTypes.EVM) {
          address = evmAddress;
        }

        if (address) {
          addresses.push({
            chainId: item.chainId,
            walletAddress: address,
          });
        }
      });
    } else if (isBrowserWallet) {
      cloudWalletList.forEach((item: any) => {
        addresses.push({
          walletAddress: item.address,
          chainId: item.chainId,
        });
      });
    }
    return addresses;
  }, [
    isThirdParty,
    isBrowserWallet,
    stxAddress,
    mnemonicChainList,
    cloudWalletList,
  ]);

  const userInfo = useMemo(() => {
    if (spotIsLogin) {
      return userInfoStore?._state?.userInfo;
    }
    return {};
  }, [spotIsLogin]);

  return {
    walletId,
    chainId: isThirdParty ? thirdPartyWallet.activeWalletHub?.evmChainId : 1,
    walletType,
    walletTypeByKey: WalletType[walletType] as keyof typeof WalletType,
    walletIsReady,
    spotIsReady,
    chainList,
    mixedChainMap,
    mnemonicChainList,
    isBrowserWallet,
    isThird: isThirdParty,
    isBybitWalletExtension: thirdPartyWallet.isBybitWalletExtension,
    thirdConnected: thirdPartyWallet.hasAddress,
    cloudConnected: userInfoStore.state.isLogin,
    userInfo,
    isLogin,
    hasWallet: isLogin && !needCreatedCloudWallet,
    evmWalletInfo: {
      address: evmAddress,
      symbol: name,
      chainId: isThirdParty ? thirdPartyWallet.activeWalletHub?.evmChainId : 1,
      pubKey: '',
    } as WalletInfo,
    isBybitWallet: BybitWalletTypes.includes(walletType),
    isCreatedCloudWallet: userCloudWalletStore.state.hasCloudWallet,
    needCreatedCloudWallet,
    evmThirdPartyProvider: thirdPartyWallet?.activeWalletHub?.EVM?.provider,
    stxThirdPartyProvider: thirdPartyWallet?.activeWalletHub?.STX?.provider,
    solanaThirdPartyProvider: thirdPartyWallet?.activeWalletHub?.SOL?.provider,
    tonThirdPartyProvider: thirdPartyWallet?.activeWalletHub?.TON?.provider,
    walletAddresses,
    getAllBalance,
    openLoginModal: () => {
      connectWalletShow();
    },
    openSpotLoginModal: () => {
      userInfoStore?.login();
    },
    createWallet: () => {
      createWalletShow();
    },
    connectBybitWallet: (walletType: WalletType[]) => {
      switchToBybitExtension(walletType[0]);
    },
    disconnectThird: () => {
      thirdPartyWallet?.dispose();
    },
    switchToBrowser: () => {
      switchToBrowser();
    },
    switchToThirdParty: () => {
      switchToThirdParty();
    },
    switchChain: (chainId: number) => {
      return thirdPartyWallet?.activeWalletHub?.EVM?.switchChain(chainId);
    },
  };
};
